import { FormProvider, useForm } from 'react-hook-form';

import { useEffect, useMemo } from 'react';

import { styled } from '@mui/material';

import { normalizeConfig } from '@app-velocity/common';

import { useI18n } from '@app-velocity/i18n';
import {
  Button,
  Form,
  Modal,
  useModalContext,
  useModalMenuContext,
} from '@app-velocity/ui';
import { ClassValidatorResolver } from '@app-velocity/validators';

import { ReportConfigDto } from '../../../../../../dto/ReportConfigDto';

import { useReportConfigModalContext } from '../../../../context/useReportConfigModalContext';
import { useSaveScopedReportConfigMutation } from '../../../../../../../../query/useSaveScopedReportConfigMutation';
import { useDeleteScopedReportConfigMutation } from '../../../../../../../../query/useDeleteScopedReportConfigMutation';
import { ConfigurationDescription } from '../../../ConfigurationDescription';
import { ConfigForm } from '../ConfigForm';
import { useGetScopedReportConfigQuery } from '../../../../../../../../query/useGetScopedReportConfigQuery';

const StyledMainContent = styled('div')({
  '@media screen and (min-width: 1024px)': {
    width: 520,
  },
});

export const ReportConfigSection = () => {
  const { t } = useI18n();
  const { closeModal } = useModalContext();
  const { selectedMenuKey } = useModalMenuContext();
  const { workspaceId, prototypeId, reportType, mode } =
    useReportConfigModalContext();

  const methods = useForm<ReportConfigDto>({
    mode: 'onSubmit',
    resolver: ClassValidatorResolver(ReportConfigDto),
  });

  const scope = useMemo(() => {
    if (mode === 'report') {
      return reportType === 'vibe' ? 'WEBSITE' : 'FIGMA_PROTOTYPE';
    }

    switch (selectedMenuKey) {
      case 'figma_prototypes':
        return 'FIGMA_PROTOTYPE' as const;
      case 'vibe_prototypes':
        return 'WEBSITE' as const;
    }
  }, [mode, reportType, selectedMenuKey]);

  const { data: rawConfig, isLoading } = useGetScopedReportConfigQuery(
    scope,
    workspaceId,
    prototypeId
  );

  const { config } = useMemo(() => normalizeConfig(rawConfig), [rawConfig]);

  const {
    mutate: save,
    isPending: isSaving,
    isSuccess: isSaved,
  } = useSaveScopedReportConfigMutation(scope!, workspaceId, prototypeId);

  const { mutate: deleteConfig, isPending: isDeleting } =
    useDeleteScopedReportConfigMutation(scope!, workspaceId, prototypeId);

  const {
    handleSubmit,
    formState: { isDirty },
  } = methods;

  const onSubmit = (data: ReportConfigDto) => {
    save(data);
  };

  useEffect(() => {
    if (!isSaved || mode === 'report') return;

    closeModal();
  }, [isSaved, closeModal, mode]);

  useEffect(() => {
    methods.reset(config);
  }, [config, methods]);

  const onReset = () => {
    // if (!tabState.hasOwnConfig) return;
    deleteConfig();
  };

  const title = useMemo(() => {
    switch (selectedMenuKey) {
      case 'figma_prototypes':
        return 'prototypes:report.settings.config.figma_prototypes_title';
      case 'vibe_prototypes':
        return 'prototypes:report.settings.config.vibe_prototypes_title';
      case 'report':
      default:
        return 'prototypes:report.settings.config.title';
    }
  }, [selectedMenuKey]);

  const description = useMemo(() => {
    switch (selectedMenuKey) {
      case 'figma_prototypes':
        return 'prototypes:report.settings.config.figma_prototypes_editing_description';
      case 'vibe_prototypes':
        return 'prototypes:report.settings.config.vibe_prototypes_editing_description';
      case 'report':
      default:
        return 'prototypes:report.settings.config.editing_description';
    }
  }, [selectedMenuKey]);

  const isPending = isSaving || isDeleting || isLoading;
  const showMinHumanParticipants = scope !== 'WEBSITE';

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <StyledMainContent>
          <Modal.Header title={title} />
          <Modal.Content>
            <ConfigurationDescription
              config={rawConfig}
              scope={scope!}
              mode={mode as 'admin' | 'report'}
              descriptionKey={description}
            />
            <ConfigForm showMinHumanParticipants={showMinHumanParticipants} />
          </Modal.Content>
        </StyledMainContent>
        <Modal.Controls>
          <Button
            variant="contained"
            color="secondary"
            type="button"
            disabled={isPending}
            onClick={onReset}
          >
            {t('prototypes:report.settings.config.reset')}
          </Button>
          {isDirty ? (
            <Button variant="contained" type="submit" disabled={isPending}>
              {mode !== 'report'
                ? t('shared:save_and_close')
                : t('shared:save')}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              type="button"
              disabled={isPending}
              onClick={() => closeModal()}
            >
              {t('shared:close')}
            </Button>
          )}
        </Modal.Controls>
      </Form>
    </FormProvider>
  );
};
